/**
 * Recall tab — what the agent referenced while shaping this variable.
 *
 * Surfaces ``manifest.knowledge_applied`` as a clickable list so the
 * researcher can audit which knowledge entries (clinical defs, data
 * findings, snippets, concept-group bindings) the agent leaned on
 * before committing the draft. Each row is a button that opens the
 * Knowledge browser at the cited entry — same target the header's
 * ``KnowledgeCitationsChip`` jumps to, but expanded into the tab so the
 * full trail is visible without a hover.
 *
 * Operational standing rules (``granularity === 'global_rule'``) are
 * filtered out — they're agent-behavior plumbing, not domain choices the
 * epi has agency over. Same rule the citation chip applies.
 *
 * Tab is hidden when no domain knowledge was applied; see
 * ``tabsForArtifact``.
 *
 * Memory: this tab will also list saved memory entries the agent cited
 * (``cite_memory`` calls) once that wire field reaches ``EpiArtifact``.
 * Pre-wired comment in place so the section structure is stable when
 * the data lands.
 */

import { Box, ButtonBase, Stack, Typography } from '@mui/material';

import { useKnowledgeBrowserStore } from '../../state/knowledgeBrowserStore';
import {
  isOperationalGlobalRule,
  type EpiAppliedKnowledge,
  type EpiArtifact,
} from '../../types/epi-chat-types';

export const RecallTab = ({ artifact }: { artifact: EpiArtifact }) => {
  const openBrowser = useKnowledgeBrowserStore((s) => s.open);
  const allEntries = artifact.knowledgeApplied ?? [];
  const entries = allEntries.filter((entry) => !isOperationalGlobalRule(entry));

  if (entries.length === 0) {
    return (
      <Box sx={{ padding: 2 }} data-testid="artifact-recall-tab-empty">
        <Typography variant="body2" color="text.secondary">
          No knowledge entries were cited while drafting this variable. The agent will populate this
          tab when it recalls a clinical definition, dataset finding, snippet, or concept-group
          binding during the build.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ padding: 2 }} data-testid="artifact-recall-tab">
      <Typography variant="body2" color="text.secondary" sx={{ marginBottom: 1.25 }}>
        Knowledge the agent recalled while drafting this variable. Each entry shaped a choice in the
        SQL or the concept selections — click through to read the source.
      </Typography>
      <Stack>
        {entries.map((entry) => (
          <RecallRow key={entry.id} entry={entry} onOpen={() => openBrowser(entry.id)} />
        ))}
      </Stack>
    </Box>
  );
};

const RecallRow = ({ entry, onOpen }: { entry: EpiAppliedKnowledge; onOpen: () => void }) => (
  <ButtonBase
    onClick={onOpen}
    data-testid={`artifact-recall-entry-${entry.id}`}
    sx={{
      display: 'block',
      width: '100%',
      textAlign: 'left',
      paddingY: 1.25,
      paddingX: 0.5,
      borderRadius: 0.5,
      borderTop: (theme) => `1px solid ${theme.palette.divider}`,
      '&:first-of-type': { borderTop: 0, paddingTop: 0 },
      '&:hover': {
        backgroundColor: (theme) => theme.palette.action.hover,
      },
    }}
  >
    <Stack spacing={0.25}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {entry.title}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {[entry.kind, entry.scope, entry.source].filter(Boolean).join(' · ')}
      </Typography>
    </Stack>
  </ButtonBase>
);
